import { getStoreInstance, setupOrganizationsInStore } from '../store';
import { Button } from '../components/ui/button';
import config from '../config';
import CorniceBrowser from '../components/CorniceBrowser';
import Head from 'next/head';
import Link from 'next/link';
import Logo from '../components/Logo';

const FUNZIONI = [
  {
    id: 'scadenzario',
    titolo: 'Lo scadenzario dei pagamenti, mese per mese',
    testo:
      'Ogni mese ha il suo quadro: incassato, da incassare e saldo per ogni inquilino. Registri un pagamento in due clic e i conti si aggiornano da soli.',
    img: 'scadenzario.png',
    alt: 'Scadenzario degli affitti di Dimora: importi pagati e non pagati del mese',
    indirizzo: 'dimora.local/affitti/2026.08'
  },
  {
    id: 'inquilini',
    titolo: 'Inquilini e contratti in una scheda sola',
    testo:
      'Contratto, durata, immobile occupato e stato della locazione: la storia di ogni inquilino a colpo d’occhio, senza fogli di calcolo né cartelle sparse.',
    img: 'inquilini.png',
    alt: 'Schede inquilini di Dimora con contratto, durata e immobile collegato',
    indirizzo: 'dimora.local/inquilini'
  },
  {
    id: 'contabilita',
    titolo: 'Contabilità pronta per il commercialista',
    testo:
      'Inquilini in ingresso e in uscita, cauzioni e pagamenti dell’anno, raccolti per esercizio ed esportabili in CSV quando servono.',
    img: 'contabilita.png',
    alt: 'Contabilità annuale di Dimora con cauzioni e pagamenti esportabili',
    indirizzo: 'dimora.local/contabilita/2026'
  }
];

const PASSI = [
  {
    numero: '1',
    titolo: 'Registra immobili e contratti',
    testo:
      'Inserisci gli immobili, gli inquilini e le condizioni del contratto: canone, durata, cauzione e spese.'
  },
  {
    numero: '2',
    titolo: 'Segui gli affitti mese per mese',
    testo:
      'Lo scadenzario ti dice chi ha pagato e chi è in ritardo. Registri i pagamenti e invii i promemoria da un unico posto.'
  },
  {
    numero: '3',
    titolo: 'Genera ricevute e documenti',
    testo:
      'Ricevute già impaginate in PDF, pronte da scaricare o inviare all’inquilino insieme agli altri documenti della locazione.'
  }
];

export default function Index() {
  return (
    <div className="min-h-screen bg-body text-foreground">
      <Head>
        <title>Dimora — Gestionale per locazioni immobiliari</title>
        <meta
          name="description"
          content="Dimora tiene in ordine affitti, inquilini, contratti e ricevute. Prova la demo con dati di esempio: nessuna registrazione."
        />
      </Head>

      {/* Intestazione */}
      <header className="sticky top-0 z-40 border-b border-border bg-card">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-5">
          <Logo size="sm" />
          <nav className="hidden items-center gap-6 text-sm text-muted-foreground md:flex">
            <a href="#funzionalita" className="hover:text-foreground">
              Funzionalità
            </a>
            <a href="#come-funziona" className="hover:text-foreground">
              Come funziona
            </a>
            <Link href="/contatti" className="hover:text-foreground">
              Contatti
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" className="hidden sm:inline-flex">
              <Link href="/signin">Accedi</Link>
            </Button>
            <Button asChild data-cy="ctaDemoHeader">
              <Link href="/signin">Prova la demo</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Eroe */}
        <section className="mx-auto w-full max-w-6xl px-5 pb-16 pt-14 text-center md:pt-20">
          <p className="text-sm font-medium uppercase tracking-widest text-primary">
            Gestionale per locazioni
          </p>
          <h1 className="mx-auto mt-4 max-w-3xl text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
            Affitti, inquilini e ricevute. Tutto in ordine.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
            Dimora tiene insieme immobili, contratti e pagamenti: sai sempre
            chi ha pagato, chi è in ritardo e cosa scade questo mese.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="h-12 px-8 text-base shadow-lg"
              data-cy="ctaDemoEroe"
            >
              <Link href="/signin">Prova la demo</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-8">
              <Link href="/signin">Accedi</Link>
            </Button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Dati di esempio, modificabili liberamente. Nessuna registrazione.
          </p>

          <CorniceBrowser
            src={`${config.BASE_PATH}/screens/cruscotto.png`}
            alt="Cruscotto di Dimora: affitti non pagati, tasso di occupazione e ricavi dell'anno"
            indirizzo="dimora.local/cruscotto"
            className="mx-auto mt-12 max-w-5xl"
            eager
          />
        </section>

        {/* Funzionalità: blocchi alternati con ritagli di UI reale */}
        <section
          id="funzionalita"
          className="scroll-mt-16 border-t border-border bg-card"
        >
          <div className="mx-auto w-full max-w-6xl space-y-20 px-5 py-20">
            {FUNZIONI.map((f, i) => (
              <div
                key={f.id}
                className={`flex flex-col items-center gap-8 lg:gap-14 ${
                  i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'
                }`}
              >
                <div className="w-full lg:w-2/5">
                  <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
                    {f.titolo}
                  </h2>
                  <p className="mt-4 text-muted-foreground">{f.testo}</p>
                </div>
                <CorniceBrowser
                  src={`${config.BASE_PATH}/screens/${f.img}`}
                  alt={f.alt}
                  indirizzo={f.indirizzo}
                  className="w-full lg:w-3/5"
                />
              </div>
            ))}
          </div>
        </section>

        {/* Come funziona */}
        <section
          id="come-funziona"
          className="mx-auto w-full max-w-6xl scroll-mt-16 px-5 py-20"
        >
          <h2 className="text-center text-2xl font-semibold tracking-tight md:text-3xl">
            Come funziona
          </h2>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {PASSI.map((p) => (
              <div key={p.numero}>
                <div className="grid size-10 place-items-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
                  {p.numero}
                </div>
                <h3 className="mt-4 text-lg font-semibold">{p.titolo}</h3>
                <p className="mt-2 text-muted-foreground">{p.testo}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Invito finale */}
        <section className="border-t border-border bg-card">
          <div className="mx-auto w-full max-w-6xl px-5 py-20 text-center">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Guardalo con i tuoi occhi
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              La demo è un ambiente completo, già popolato con immobili,
              inquilini e pagamenti. Entri con un clic ed esplori tutto.
            </p>
            <Button
              asChild
              size="lg"
              className="mt-8 h-14 px-10 text-lg shadow-lg"
              data-cy="ctaDemoFinale"
            >
              <Link href="/signin">Prova la demo</Link>
            </Button>
            <p className="mt-3 text-sm text-muted-foreground">
              Dati di esempio, modificabili liberamente.
            </p>
          </div>
        </section>
      </main>

      {/* Piede da prodotto */}
      <footer className="border-t border-border">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-5 py-14 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <Logo size="sm" />
            <p className="mt-3 max-w-[16rem] text-sm text-muted-foreground">
              Il gestionale che tiene in ordine locazioni, inquilini e
              ricevute.
            </p>
          </div>
          <div>
            <div className="text-sm font-semibold">Prodotto</div>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <a href="#funzionalita" className="hover:text-foreground">
                  Funzionalità
                </a>
              </li>
              <li>
                <a href="#come-funziona" className="hover:text-foreground">
                  Come funziona
                </a>
              </li>
              <li>
                <Link href="/signin" className="hover:text-foreground">
                  Prova la demo
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold">Risorse</div>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link href="/contatti" className="hover:text-foreground">
                  Contatti
                </Link>
              </li>
              <li>
                <a
                  href="https://github.com/microrealestate/microrealestate"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-foreground"
                >
                  Progetto open source (MIT)
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold">Legale</div>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                <Link href="/privacy" className="hover:text-foreground">
                  Privacy
                </Link>
              </li>
              <li>
                <Link href="/termini" className="hover:text-foreground">
                  Termini d&apos;uso
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-2 px-5 py-5 text-xs text-muted-foreground">
            <span>
              © 2026 Dimora — ambiente dimostrativo di Giuseppe Delli Paoli
            </span>
            <span>Versione 1.0.0 · basato su microrealestate</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Pagina pubblica: _app la rende fuori dallo store, così l'HTML arriva
// già completo dal server (le pagine dentro InjectStoreContext escono vuote).
Index.paginaPubblica = true;

export async function getServerSideProps(context) {
  // Chi è già autenticato salta la pagina pubblica e va al cruscotto.
  const store = getStoreInstance();

  const { status } = await store.user.refreshTokens(context);
  if (status !== 200) {
    return { props: {} };
  }

  await setupOrganizationsInStore();
  if (!store.user.signedIn || !store.organization.selected?.name) {
    return { props: {} };
  }

  return {
    redirect: {
      destination: `/${encodeURIComponent(
        store.organization.selected.name
      )}/dashboard`,
      permanent: false
    }
  };
}
